import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchTenantSettings,
  fetchCompetitorSettings,
  updateTenantSettings,
  upsertCompetitorSettings,
  type UpdateTenantSettingsParams,
  type UpsertCompetitorSettingsParams,
} from "@/features/opportunities/api/tenant-settings.api";
import { useAuth } from "@/features/auth/hooks/useAuth";

// ─── Key Factory ────────────────────────────────────────────────────

export const tenantSettingsKeys = {
  all: (tenantId: string) => ["tenant-settings", tenantId] as const,
  settings: (tenantId: string) => ["tenant-settings", tenantId, "settings"] as const,
  competitors: (tenantId: string) => ["tenant-settings", tenantId, "competitors"] as const,
} as const;

// ─── Query Options ──────────────────────────────────────────────────

export function tenantSettingsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: tenantSettingsKeys.settings(tid || "__none__"),
    queryFn: fetchTenantSettings,
    enabled: !!tid,
    staleTime: 0,
  });
}

export function competitorSettingsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: tenantSettingsKeys.competitors(tid || "__none__"),
    queryFn: fetchCompetitorSettings,
    enabled: !!tid,
    staleTime: 0,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: UpdateTenantSettingsParams) => updateTenantSettings(params),
    onSuccess: () => {
      const activeTenantId = useAuth.getState().activeTenantId;
      if (activeTenantId) {
        void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.settings(activeTenantId) });
      }
    },
  });
}

export function useUpsertCompetitorSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ industry, params }: { industry: string; params: UpsertCompetitorSettingsParams }) =>
      upsertCompetitorSettings(industry, params),
    onSuccess: () => {
      const activeTenantId = useAuth.getState().activeTenantId;
      if (activeTenantId) {
        void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.competitors(activeTenantId) });
      }
    },
  });
}
